'use client';
import { Avatar, AvatarImage } from '@ahafood/ui/components/avatar';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { cn } from '@ahm/common-helpers';
import { motion } from 'framer-motion';
import React, { useMemo } from 'react';

import { useGetFoodOrderCategories } from '@/api/ahafood/get-category-fn';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile';
import { useAuthStore } from '@/store';

type SolomealCategoryProps = {
  selectedCategory?: string;
  onCategoryChange?: (category: string | undefined) => void;
};

export default function SolomealCategory({
  selectedCategory,
  onCategoryChange,
}: SolomealCategoryProps) {
  const isMobile = useIsMobile();
  const { user } = useAuthStore();

  const { data: categoriesData, isLoading } = useGetFoodOrderCategories({
    type: 'SOLO_MEAL',
    limit: 20,
  });

  const categoryItems = useMemo(() => {
    if (!categoriesData?.categories) return null;

    const allCategory = {
      id: 'all',
      slug: '',
      title_vi_vn: 'Tất cả',
      icon_img_url: '/static/icons/solomeal-category.svg',
    };

    const handleClick = (categorySlug: string) => {
      trackEvent('click_button', {
        action_time: Math.floor(Date.now() / 1000),
        button_name: 'solomeal_category_toggle',
        selected_category: categorySlug,
        user_id: user?.cunstomerId,
      });

      // Toggle category selection
      const newCategory =
        selectedCategory === categorySlug ? undefined : categorySlug;
      onCategoryChange?.(newCategory);
    };

    const categories = [allCategory, ...categoriesData.categories];

    return (
      <div
        className="flex gap-1 pr-4 pl-2"
        data-testid="solomeal-category-list"
      >
        {categories.map((category) => {
          const isSelected =
            selectedCategory === category.slug ||
            (selectedCategory === undefined && category.slug === '');

          return (
            <div
              key={category.id}
              className="shrink-0"
              style={{
                width: isMobile ? 90 : 120,
                height: 'fit-content',
              }}
              data-testid="solomeal-category-item"
              data-selected={isSelected ? 'true' : undefined}
            >
              <button
                onClick={() => handleClick(category.slug)}
                className="group block w-full bg-transparent border-none p-0"
              >
                <div
                  className={cn(
                    'relative flex flex-col items-center justify-center space-y-2 p-2'
                  )}
                  data-category={category.slug}
                >
                  <motion.div
                    whileHover={{
                      scale: 1.1,
                      transition: {
                        type: 'spring',
                        stiffness: 300,
                        damping: 10,
                      },
                    }}
                    className={cn(
                      'group relative aspect-square w-full flex-none overflow-hidden rounded-full transition-all hover:bg-white',
                      isSelected
                        ? 'ring-4 ring-primary ring-offset-2 bg-primary-10'
                        : 'hover:bg-white hover:shadow-md'
                    )}
                  >
                    {category.slug === '' ? (
                      // Special handling for "Tất cả" category with SVG
                      <img
                        src="/static/icons/solomeal-category.svg"
                        alt="Tất cả"
                        className="size-full object-cover rounded-full"
                        data-testid="solomeal-category-item-icon"
                      />
                    ) : (
                      <Avatar
                        className="scale-110 overflow-hidden rounded-full size-full"
                        data-testid="solomeal-category-item-icon"
                      >
                        <AvatarImage
                          src={
                            category.icon_img_url ||
                            '/static/images/store-empty.webp'
                          }
                          alt={category.slug}
                          style={{ objectFit: 'cover' }}
                          className="size-full"
                        />
                      </Avatar>
                    )}
                  </motion.div>
                  <h4
                    className={cn(
                      'line-clamp-2 w-full text-center text-xs font-medium md:text-sm',
                      isSelected
                        ? 'text-primary font-bold'
                        : 'text-neutral-70 group-hover:text-neutral-90'
                    )}
                    data-testid="solomeal-category-item-label"
                  >
                    {category.title_vi_vn}
                  </h4>
                </div>
              </button>
            </div>
          );
        })}
      </div>
    );
  }, [
    categoriesData?.categories,
    selectedCategory,
    onCategoryChange,
    isMobile,
    user?.cunstomerId,
  ]);

  // Show skeleton loading
  if (isLoading || !categoriesData?.categories) {
    return (
      <div className="bg-white shadow-sm md:shadow-none border-b-2 md:border-none border-neutral-20">
        <div className="mx-auto max-w-screen-xl">
          <div className="overflow-x-auto scrollbar-hide">
            <div
              className={cn(
                'md:py-4 py-2',
                isMobile ? 'h-fit items-center' : 'min-h-36'
              )}
            >
              <div className="flex gap-3 pr-4 pl-2">
                {Array.from({ length: 5 }, (_, i) => (
                  <div
                    key={i}
                    className="shrink-0"
                    style={{
                      width: isMobile ? 90 : 120,
                      height: 'fit-content',
                    }}
                  >
                    <div className="flex flex-col items-center justify-center space-y-2 p-2">
                      <Skeleton
                        className={cn(
                          'aspect-square rounded-full bg-neutral-10',
                          isMobile ? 'w-16 h-16' : 'w-20 h-20'
                        )}
                      />
                      <Skeleton className="h-4 w-16 bg-neutral-10 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!categoriesData?.categories?.length) return null;

  return (
    <div
      className="bg-white shadow-sm md:shadow-none border-b-4 md:border-none"
      data-testid="solomeal-category-section"
    >
      <div className="mx-auto max-w-screen-xl relative">
        <div className="absolute right-0 inset-y-0 z-10 overflow-hidden bg-gradient-to-r from-transparent via-white/50 to-white w-7" />

        <div
          className="overflow-x-auto scrollbar-hide"
          data-testid="solomeal-category-scroll"
        >
          <div
            className={cn(
              'md:py-4 pb-0 pt-3',
              isMobile ? 'h-fit items-center' : 'min-h-36'
            )}
          >
            {categoryItems}
          </div>
        </div>
      </div>
    </div>
  );
}
